import { selectImageOptions } from "@/store/canvas/canvasSelectors";
import { setImageOptions } from "@/store/canvas/canvasSlice";
import { type RefObject, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { BaseCanvasRef } from "../components/BaseCanvas";
import { createAddImageAction, createModifyImageAction, ImageUpdateHandler } from "../extensions/imageExtension";
import { ImageOptions } from "../types/imageTypes";
import { useSubscribeToAddObjectEvent } from "./event/useAddObjectEvent";
import { useSubscribeToChangeImageOptionsEvent } from "./event/useImageChangeOptionsEvent";

export function useImageAddingAndModification(baseCanvasRef: RefObject<BaseCanvasRef>) {
  const dispatch = useDispatch();
  const imageOptionsRef = useRef<ImageOptions | null>(null);
  const imageOptions = useSelector(selectImageOptions);

  useMemo(() => {
    imageOptionsRef.current = imageOptions;
  }, [imageOptions]);

  useSubscribeToAddObjectEvent((payload) => {
    if (payload.type !== "image") return;
    const { data } = payload;

    baseCanvasRef.current?.addObject(createAddImageAction(data));
  });

  useSubscribeToChangeImageOptionsEvent((options) => {
    if (!options) return;

    baseCanvasRef.current?.modifyObject(createModifyImageAction(options));
  });

  const handleImageOptionsUpdate: ImageUpdateHandler = (params) => {
    if (!imageOptionsRef.current) return;

    imageOptionsRef.current = {
      ...imageOptionsRef.current,
      ...params,
    };

    dispatch(setImageOptions(imageOptionsRef.current));
  };

  return {
    handleImageOptionsUpdate,
  };
}
